import { Box, Typography, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { css, StyleSheet } from "aphrodite";
import { useSelector } from "react-redux";

import CustomAutoComplete from "../../../../components/CustomAutoComplete";
import InputField from "../../../../components/common/new/InputField/InputField";
import IntroductionFormApi from "../../../../services/api/introductionForm";
import { NEW_FRIEND_FIELDS } from "../../../../contants/formFields";
import SwitchButton from "../../../../components/common/SwitchButton/SwitchButton";
import Styles from "./styles";
import AutoCompleteDropdown from "../../../../components/common/new/AutoCompleteDropdown/AutoCompleteDropdown";
import { getOptions } from "../../../../utils";
import { getSubDivisionDisplayName } from "../../../../utils/helper";
import { selectAttendanceMeetingType } from "../../../../redux/selectors/attendance";

function CreateNewFriend({ handleChange, errors, values, setFieldValue }) {
  const [divisions, setDivisions] = useState([]);
  const [subDivisions, setSubDivisions] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [profession, setProfession] = useState([]);
  const [toggle, settoggle] = useState(false);

  const FD_DIVISIONS = [52, 53];

  const [friendName, setFriendName] = useState("");
  const [address, setAddress] = useState("");
  const [pincode, setPincode] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [introducerName, setIntroducerName] = useState("");
  const [introducerContact, setIntroducerContact] = useState("");

  const isMobile = useMediaQuery("(max-width: 768px)");
  const meetingType = useSelector(selectAttendanceMeetingType);

  useEffect(() => {
    const init = async () => {
      const [divRes, stRes, cRes, pRes] = await Promise.all([
        IntroductionFormApi.getDivisions(),
        IntroductionFormApi.getStates(),
        IntroductionFormApi.getCountries(),
        IntroductionFormApi.getProfessions(),
      ]);

      setDivisions(divRes.data || []);
      setStates(stRes.data || []);
      setCountries(cRes.data || []);
      setProfession(pRes.data || []);
    };

    init();
  }, []);

  // helpers
  const normalize = (v) =>
    String(v || "")
      .toLowerCase()
      .trim();

  // substring match (generic)
  const isNameLike = (name = "", tokens = []) => {
    const nm = normalize(name);
    return tokens.some((t) => nm.includes(normalize(t)));
  };

  // ✅ exact token match (fix for MD vs YMD)
  // splits by non-alphanumeric: "YMD - MD Pillar" => ["ymd","md","pillar"]
  const hasExactToken = (name = "", token = "") => {
    const parts = normalize(name)
      .split(/[^a-z0-9]+/)
      .filter(Boolean);
    return parts.includes(normalize(token));
  };

  const getDivisionIdsByExactTokens = (tokens) =>
    divisions
      ?.filter((d) => tokens.some((t) => hasExactToken(d?.name, t)))
      ?.map((d) => d.divisionId) || [];

  const allowedDivisionIds = useMemo(() => {
    if (!divisions?.length) return [];

    // Education, Arts, Healthcare → No FD divisions
    if (
      meetingType === "OTHER_MEETING_EDUCATION_DIVISION" ||
      meetingType === "OTHER_MEETING_ARTS_DIVISION" ||
      meetingType === "OTHER_MEETING_HEALTHCARE_DIVISION"
    ) {
      return divisions
        .map((d) => d.divisionId)
        .filter((id) => !FD_DIVISIONS.includes(id));
    }

    // MD Pillar → ONLY MD (not YMD)
    if (meetingType === "OTHER_MEETING_MD_PILLAR") {
      const mdIds = getDivisionIdsByExactTokens(["md"]);
      return mdIds.length ? mdIds : divisions.map((d) => d.divisionId);
    }
    if (meetingType === "OTHER_MEETING_SOURCE_OF_ORIGIN") {
      const mdIds = getDivisionIdsByExactTokens(["wd"]);
      return mdIds.length ? mdIds : divisions.map((d) => d.divisionId);
    }

    // Campus Group → YMD/YWD only
    if (meetingType === "OTHER_MEETING_CAMPUS_GROUP") {
      const yIds = getDivisionIdsByExactTokens(["ymd", "ywd"]);
      return yIds.length ? yIds : divisions.map((d) => d.divisionId);
    }

    // ✅ NEW: Hope and Friendship → YWD only
    if (meetingType === "OTHER_MEETING_YWD_HOPE_AND_FRIENDSHIP_GATHERING") {
      const ywdIds = getDivisionIdsByExactTokens(["ywd"]);
      return ywdIds.length ? ywdIds : divisions.map((d) => d.divisionId);
    }

    // ✅ NEW: Vanguard Academy → YMD only
    if (meetingType === "OTHER_MEETING_YMD_VANGUARD_ACADEMY") {
      const ymdIds = getDivisionIdsByExactTokens(["ymd"]);
      return ymdIds.length ? ymdIds : divisions.map((d) => d.divisionId);
    }

    // ✅ NEW: All India Study → All divisions (no filter)
    if (meetingType === "OTHER_MEETING_ALL_INDIA_STUDY") {
      return divisions.map((d) => d.divisionId);
    }

    // Region SI meetings → FD only
    if (
      meetingType === "CUBS_MEETING" ||
      meetingType === "YP_MEETING" ||
      meetingType === "YL_MEETING"
    ) {
      return FD_DIVISIONS;
    }

    // Student Division → YMD/YWD only
    if (meetingType === "SD_MEETING") {
      const yIds = getDivisionIdsByExactTokens(["ymd", "ywd"]);
      return yIds.length ? yIds : divisions.map((d) => d.divisionId);
    }

    // Default → All divisions
    return divisions.map((d) => d.divisionId);
  }, [divisions, meetingType]);

  const filteredDivisions = useMemo(() => {
    if (!divisions?.length) return [];
    return divisions.filter((d) => allowedDivisionIds.includes(d.divisionId));
  }, [divisions, allowedDivisionIds]);

  useEffect(() => {
    const currentDivision = values[NEW_FRIEND_FIELDS.DIVISION];
    if (!currentDivision) return;

    if (!allowedDivisionIds.includes(currentDivision)) {
      setFieldValue(NEW_FRIEND_FIELDS.DIVISION, "");
      setFieldValue(NEW_FRIEND_FIELDS.SUB_DIVISION, "");
      setSubDivisions([]);
    }
  }, [allowedDivisionIds, setFieldValue, values]);

  const filteredSubDivisions = useMemo(() => {
    if (!subDivisions?.length) return [];

    const by = (token) =>
      subDivisions.filter((sd) => isNameLike(sd?.name, [token]));

    if (meetingType === "CUBS_MEETING") return by("cubs");
    if (meetingType === "YP_MEETING") return by("phoenix");
    if (meetingType === "YL_MEETING") return by("lion");
    if (meetingType === "SD_MEETING") return by("student");

    return subDivisions;
  }, [subDivisions, meetingType]);

  useEffect(() => {
    const currentSub = values[NEW_FRIEND_FIELDS.SUB_DIVISION];
    if (!currentSub) return;

    const allowedSubIds = filteredSubDivisions.map((sd) => sd.divisionId);
    if (!allowedSubIds.includes(currentSub)) {
      setFieldValue(NEW_FRIEND_FIELDS.SUB_DIVISION, "");
    }
  }, [filteredSubDivisions, setFieldValue, values]);

  const Style = StyleSheet.create({
    gridContainer: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridTemplateRows: "1fr",
      gridRowGap: "1.5rem",
      gridColumnGap: "3.75rem",
      "@media (max-width: 768px)": {
        gridRowGap: "1rem",
        gridColumnGap: "1.5rem",
      },
    },
  });

  return (
    <Box className={css(Style.gridContainer)}>
      <div style={{ ...Styles.gridItem, ...Styles.name }}>
        <InputField
          required
          label="New Friend Name"
          name={NEW_FRIEND_FIELDS.NAME}
          value={friendName}
          style={Styles.textfieldStyle}
          onChange={(e) => {
            const { value } = e.target;
            const regex = /^[A-Za-z ]*$/;
            if (regex.test(value)) {
              if (value.startsWith(" ")) return;
              if (value.trim().length === 0 && value.length > 0) return;
              setFriendName(value);
              handleChange(e);
            }
          }}
        />
      </div>

      <div
        style={
          isMobile ? { ...Styles.gridItem, ...Styles.name } : Styles.gridItem
        }
      >
        <InputField
          required
          label="Address"
          name={NEW_FRIEND_FIELDS.ADDRESS_LINE_1}
          value={address}
          style={Styles.textfieldStyle}
          onChange={(e) => {
            const { value } = e.target;
            const regex = /^[A-Za-z0-9 ,.\-/]*$/;
            if (regex.test(value)) {
              if (value.startsWith(" ")) return;
              if (value.trim().length === 0 && value.length > 0) return;
              setAddress(value);
              handleChange(e);
            }
          }}
        />
      </div>

      <div style={Styles.gridItem}>
        <InputField
          label="Pincode"
          name={NEW_FRIEND_FIELDS.PINCODE}
          value={pincode}
          style={Styles.textfieldStyle}
          inputProps={{ maxLength: 6 }}
          onChange={(e) => {
            const { value } = e.target;
            const regex = /^\d{0,6}$/;
            if (regex.test(value)) {
              setPincode(value);
              handleChange(e);
            }
          }}
        />
      </div>

      <div style={Styles.gridItem}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => option.stateName}
          label="State"
          name={NEW_FRIEND_FIELDS.STATE}
          options={getOptions(states, "stateId")}
          error={errors[NEW_FRIEND_FIELDS.STATE]}
          value={values[NEW_FRIEND_FIELDS.STATE]}
          inputProps={{ required: true }}
          onChange={(selectedValue) => {
            if (!selectedValue) {
              setFieldValue(NEW_FRIEND_FIELDS.STATE, "");
              setCities([]);
            } else {
              setFieldValue(NEW_FRIEND_FIELDS.STATE, selectedValue.value);
              setCities(selectedValue.cities);
            }
            setFieldValue(NEW_FRIEND_FIELDS.CITY, "");
          }}
        />
      </div>

      <div style={Styles.gridItem}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => option.cityName}
          label="City"
          name={NEW_FRIEND_FIELDS.CITY}
          options={getOptions(cities, "cityId")}
          error={errors[NEW_FRIEND_FIELDS.CITY]}
          value={values[NEW_FRIEND_FIELDS.CITY]}
          disabled={!values[NEW_FRIEND_FIELDS.STATE]}
          inputProps={{ required: true }}
          onChange={(selectedValue) => {
            setFieldValue(NEW_FRIEND_FIELDS.CITY, selectedValue?.value ?? "");
          }}
        />
      </div>

      <div style={Styles.gridItem}>
        <InputField
          required
          label="Contact Number"
          name={NEW_FRIEND_FIELDS.CONTACT_NUMBER}
          value={contactNumber}
          style={Styles.textfieldStyle}
          inputProps={{ maxLength: 10 }}
          onChange={(e) => {
            const { value } = e.target;
            const regex = /^\d{0,10}$/;
            if (regex.test(value)) {
              setContactNumber(value);
              handleChange(e);
            }
          }}
        />
      </div>

      <div style={Styles.gridItem}>
        <InputField
          label="Email Address"
          name={NEW_FRIEND_FIELDS.EMAIL_ADDRESS}
          onChange={handleChange}
        />
      </div>

      <div style={Styles.gridItem}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => option.name}
          label="Division"
          name={NEW_FRIEND_FIELDS.DIVISION}
          options={getOptions(filteredDivisions, "divisionId")}
          error={errors[NEW_FRIEND_FIELDS.DIVISION]}
          value={values[NEW_FRIEND_FIELDS.DIVISION]}
          inputProps={{ required: true }}
          onChange={(selectedValue) => {
            if (!selectedValue) {
              setFieldValue(NEW_FRIEND_FIELDS.DIVISION, "");
              setSubDivisions([]);
            } else {
              setFieldValue(NEW_FRIEND_FIELDS.DIVISION, selectedValue.value);
              setSubDivisions(selectedValue.subDivisions);
            }
            setFieldValue(NEW_FRIEND_FIELDS.SUB_DIVISION, "");
          }}
        />
      </div>

      <div style={Styles.gridItem}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => getSubDivisionDisplayName(option.name)}
          label="Sub Division"
          name={NEW_FRIEND_FIELDS.SUB_DIVISION}
          options={getOptions(filteredSubDivisions, "divisionId")}
          error={errors[NEW_FRIEND_FIELDS.SUB_DIVISION]}
          value={values[NEW_FRIEND_FIELDS.SUB_DIVISION]}
          disabled={!values[NEW_FRIEND_FIELDS.DIVISION]}
          inputProps={
            FD_DIVISIONS.includes(values[NEW_FRIEND_FIELDS.DIVISION])
              ? { required: true }
              : { required: false }
          }
          onChange={(selectedValue) => {
            setFieldValue(
              NEW_FRIEND_FIELDS.SUB_DIVISION,
              selectedValue?.value ?? ""
            );
          }}
        />
      </div>

      {values[NEW_FRIEND_FIELDS.DIVISION] === 52 ||
      values[NEW_FRIEND_FIELDS.DIVISION] === 53 ? (
        <>
          <div style={Styles.gridItem}>
            <InputField
              required
              label="Parent's Contact Number"
              name={NEW_FRIEND_FIELDS.PARENT_CONTACT_NUMBER}
              value={values[NEW_FRIEND_FIELDS.PARENT_CONTACT_NUMBER] || ""}
              onChange={handleChange}
              error={errors[NEW_FRIEND_FIELDS.PARENT_CONTACT_NUMBER]}
              inputProps={{ maxLength: 10 }}
            />
          </div>
          <div style={Styles.gridItem}>
            <InputField
              required
              label="Parent's Email Address"
              name={NEW_FRIEND_FIELDS.PARENT_EMAIL_ADDRESS}
              value={values[NEW_FRIEND_FIELDS.PARENT_EMAIL_ADDRESS] || ""}
              error={errors[NEW_FRIEND_FIELDS.PARENT_EMAIL_ADDRESS]}
              onChange={handleChange}
            />
          </div>
        </>
      ) : null}

      <div style={Styles.gridItem}>
        <InputField
          placeholder="Date of Birth"
          label="Date of Birth"
          type="date"
          name={NEW_FRIEND_FIELDS.DATE_OF_BIRTH}
          value={values[NEW_FRIEND_FIELDS.DATE_OF_BIRTH] || ""}
          onChange={handleChange}
          InputLabelProps={{ shrink: true }} // ✅ label overlap fix
          InputProps={{
            startAdornment: null,
          }}
        />
      </div>

      <div style={Styles.gridItem}>
        <AutoCompleteDropdown
          label="Profession"
          variant="standard"
          value={values[NEW_FRIEND_FIELDS.PROFESSION]}
          name={NEW_FRIEND_FIELDS.PROFESSION}
          error={errors[NEW_FRIEND_FIELDS.PROFESSION]}
          getOptionLabel={(option) => option.profession}
          options={getOptions(profession, "professionsId")}
          inputProps={{ required: true }}
          onChange={(selectedValue) => {
            setFieldValue(
              NEW_FRIEND_FIELDS.PROFESSION,
              selectedValue?.value ?? ""
            );
          }}
        />
      </div>

      <div style={Styles.gridItem}>
        <AutoCompleteDropdown
          getOptionLabel={(option) => option.countryName}
          name={NEW_FRIEND_FIELDS.NATIONALITY_ID}
          options={getOptions(countries, "countryId")}
          inputProps={{ required: true }}
          error={errors[NEW_FRIEND_FIELDS.NATIONALITY_ID]}
          value={values[NEW_FRIEND_FIELDS.NATIONALITY_ID]}
          label="Nationality/Country"
          onChange={(selectedValue) => {
            setFieldValue(
              NEW_FRIEND_FIELDS.NATIONALITY_ID,
              selectedValue?.value ?? ""
            );
          }}
        />
      </div>

      <div style={Styles.gridItem} />

      <div
        style={
          isMobile ? { ...Styles.gridItem, ...Styles.name } : Styles.gridItem
        }
      >
        <Typography
          style={{ fontSize: 12, color: " #8F92A1", fontWeight: 700 }}
        >
          Do you want to find the name of the introducer?
        </Typography>

        <Box sx={{ pt: "1rem" }}>
          <SwitchButton
            value={toggle}
            handleChange={(e) => {
              settoggle(e.target.checked);
              setFieldValue(NEW_FRIEND_FIELDS.INTRODUCED_BY_NAME, "");
              setFieldValue(NEW_FRIEND_FIELDS.INTRODUCED_BY_EMAIL, "");
              setFieldValue(NEW_FRIEND_FIELDS.INTRODUCED_BY_MOBILE_NUMBER, "");
              setFieldValue(NEW_FRIEND_FIELDS.INTRODUCED_BY, "");
            }}
          />
        </Box>
      </div>

      <div style={Styles.gridItem}>
        {toggle ? (
          <CustomAutoComplete
            required
            api={IntroductionFormApi.searchMembers}
            placeholder={toggle ? "Search an introducer" : ""}
            name={NEW_FRIEND_FIELDS.INTRODUCED_BY_NAME}
            setFieldValue={setFieldValue}
            error={errors[NEW_FRIEND_FIELDS.INTRODUCED_BY_NAME]}
            label="Introducer's Name"
            optionLabelId="fullName"
            optionValueId="fullName"
            withParam="memberName"
            selectedValue={(val) => {
              if (val.fullName)
                setFieldValue(
                  NEW_FRIEND_FIELDS.INTRODUCED_BY_NAME,
                  String(val.fullName)
                );
              if (val.memberId)
                setFieldValue(NEW_FRIEND_FIELDS.INTRODUCED_BY, val.memberId);
              if (val.emailId)
                setFieldValue(
                  NEW_FRIEND_FIELDS.INTRODUCED_BY_EMAIL,
                  String(val.emailId)
                );
              if (val.mobileNumber)
                setFieldValue(
                  NEW_FRIEND_FIELDS.INTRODUCED_BY_MOBILE_NUMBER,
                  String(val.mobileNumber)
                );
            }}
            isServerSide
            value={values[NEW_FRIEND_FIELDS.INTRODUCED_BY_NAME]}
            options={[]}
          />
        ) : (
          <InputField
            required
            label="Introducer's Name"
            name={NEW_FRIEND_FIELDS.INTRODUCED_BY_NAME}
            value={introducerName}
            style={Styles.textfieldStyle}
            onChange={(e) => {
              const { value } = e.target;
              const regex = /^[A-Za-z ]*$/;
              if (regex.test(value)) {
                if (value.startsWith(" ")) return;
                if (value.trim().length === 0 && value.length > 0) return;
                setIntroducerName(value);
                handleChange(e);
              }
            }}
          />
        )}
      </div>

      <div style={Styles.gridItem}>
        <InputField
          label="Introducer's Contact Number"
          required
          name={NEW_FRIEND_FIELDS.INTRODUCED_BY_MOBILE_NUMBER}
          value={introducerContact}
          style={Styles.textfieldStyle}
          inputProps={{ maxLength: 10 }}
          disabled={toggle}
          onChange={(e) => {
            const { value } = e.target;
            const regex = /^\d{0,10}$/;
            if (regex.test(value)) {
              setIntroducerContact(value);
              handleChange(e);
            }
          }}
        />
      </div>

      <div style={Styles.gridItem}>
        <InputField
          label="Introducer's Email Address"
          name={NEW_FRIEND_FIELDS.INTRODUCED_BY_EMAIL}
          value={values[NEW_FRIEND_FIELDS.INTRODUCED_BY_EMAIL] || ""}
          onChange={handleChange}
          disabled={toggle}
        />
      </div>
    </Box>
  );
}

CreateNewFriend.propTypes = {
  handleChange: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  values: PropTypes.shape({
    [NEW_FRIEND_FIELDS.DIVISION]: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    [NEW_FRIEND_FIELDS.SUB_DIVISION]: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    [NEW_FRIEND_FIELDS.STATE]: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    [NEW_FRIEND_FIELDS.CITY]: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    [NEW_FRIEND_FIELDS.NATIONALITY_ID]: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    [NEW_FRIEND_FIELDS.DATE_OF_BIRTH]: PropTypes.string,
    [NEW_FRIEND_FIELDS.PROFESSION]: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
    [NEW_FRIEND_FIELDS.PARENT_CONTACT_NUMBER]: PropTypes.string,
    [NEW_FRIEND_FIELDS.PARENT_EMAIL_ADDRESS]: PropTypes.string,
    [NEW_FRIEND_FIELDS.INTRODUCED_BY_NAME]: PropTypes.string,
    [NEW_FRIEND_FIELDS.INTRODUCED_BY_EMAIL]: PropTypes.string,
    [NEW_FRIEND_FIELDS.INTRODUCED_BY_MOBILE_NUMBER]: PropTypes.string,
    [NEW_FRIEND_FIELDS.INTRODUCED_BY]: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string,
    ]),
  }).isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

CreateNewFriend.defaultProps = {
  errors: {},
};

export default CreateNewFriend;
